import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { unstable_batchedUpdates as batch } from 'react-dom'
import debounce from 'lodash.debounce'
import * as R from 'ramda'
import {
  BannedUser,
  Subscriber,
  Subscription,
  UserPermission,
} from '@weblite/ts-action/subscription'
import { RootState } from '$setup/redux'
import { useHall } from '$logic/hall/react-hook'
import { createUseEntity } from '$func/create-use-entity.react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { useSearchedUserByIds } from '$logic/users'
import { MemberPermissions } from '$logic/types/subscription'
import { useRoomAttribute } from '$logic/room/react-hook'
import { getHall } from '$logic/hall'
import hostConf from '$config/frontend'
import {
  AddToOpenedUserList,
  DEFAULT_OPEN_LIST_TYPE,
  resetOpenedUserListSearch,
  useOpenedUserListAsyncIntegration,
  useOpenedUserListItems,
  useOpenedUserListSearchedItems,
} from '$logic/opened-user-list'
import {
  getHallAdmins,
  getHallExceptionMembers,
  getHallRemovedSubscribers,
  getHallSubscribers,
  getSubscriptionDate,
  searchInSubscribers,
} from './other.service'
import { decodeMemberPermissions, getSubscription } from './client.service'
import { isClientsRoom } from '$logic/room'

const getSubscriptions = (state: RootState): Record<string, Subscription> =>
  state.main.subscriptions

const getSubscriptionView = (roomId: string) => (state: RootState) =>
  getSubscriptions(state)[roomId]

const getSubscriptionWithinClientRooms = (roomId: string) => {
  if (!isClientsRoom(roomId)) return Promise.resolve<Subscription>(undefined)
  return getSubscription(roomId)
}

export const useSubscription = createUseEntity<Subscription>(
  getSubscriptionView,
  getSubscriptionWithinClientRooms,
)

export function useSubscriptions() {
  return useSelector(getSubscriptions)
}
export function useSubscriptionSync(roomId: string) {
  return useSelector(getSubscriptionView(roomId))
}

export function useSubscriptionSyncPermissions(roomId: string) {
  return useSelector(getSubscriptionView(roomId))?.permissions
}

export function useHallAdmins(hallId: string, key = DEFAULT_OPEN_LIST_TYPE) {
  return useOpenedUserListAsyncIntegration(
    () => getHallAdmins(hallId),
    [hallId],
    key,
  )
}

export function useHallAdminsCount(hallId: string) {
  const { value, loading } = useAsyncFunction(
    () => getHallAdmins(hallId),
    [hallId],
  )

  return { count: (value || []).length, loading }
}

export function useSearchedHallAdmins<T extends { id: string }>({
  query,
  fetchedItems,
}: {
  query: string
  fetchedItems: T[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds(
    query,
    fetchedItems.map(admin => admin.id),
  )
  return {
    loading,
    searchedItems: searchedItems.map(id =>
      fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useHallSubscribers(hallId: string) {
  const items = useOpenedUserListItems()
  const [loading, setLoading] = useState<boolean>(true)
  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(true)
  const [pageToken, setPageToken] = useState<string>('')

  const loadMore = async () => {
    if (!hasMoreItemToLoad) return

    const res = await getHallSubscribers(hallId, pageToken)
    setLoading(false)

    batch(() => {
      if (!res.pageToken) setHasMoreItemToLoad(false)
      setPageToken(res.pageToken)
      AddToOpenedUserList(res.users)
    })
  }

  return { items: items as Subscriber[], hasMoreItemToLoad, loadMore, loading }
}

const DEBOUNCE_TIME = 700
const debouncedSearchInSubscribers = debounce(
  (roomId: string, query: string, fn: (res: Subscriber[]) => unknown) =>
    searchInSubscribers(roomId, query).then(fn),
  DEBOUNCE_TIME,
)

export function useSearchedSubscribers({
  roomId,
  query,
  fetchedItems = [],
  hasMoreItemToLoad,
}: {
  roomId: string
  query: string
  fetchedItems: Subscriber[]
  hasMoreItemToLoad: boolean
}) {
  const [loading, setLoading] = useState<boolean>(false)
  const searchedItems = useOpenedUserListSearchedItems() as Subscriber[]

  useEffect(() => {
    if (hasMoreItemToLoad) {
      setLoading(true)
      debouncedSearchInSubscribers(roomId, query, searchResult => {
        resetOpenedUserListSearch()
        AddToOpenedUserList(searchResult)

        setLoading(false)
      })
    }
  }, [roomId, query, hasMoreItemToLoad])

  const { searchedItems: localSearchResult } = useSearchedUserByIds(
    query,
    fetchedItems.map(item => item.id),
  )

  return {
    loading,
    searchedItems:
      loading || !hasMoreItemToLoad
        ? localSearchResult.map(id => fetchedItems.find(item => item.id === id))
        : searchedItems,
  }
}

export function useHallRemovedSubscribers(
  hallId: string,
  key = DEFAULT_OPEN_LIST_TYPE,
) {
  const { loading, items } = useOpenedUserListAsyncIntegration(
    () => getHallRemovedSubscribers(hallId),
    [hallId],
    key,
  )

  return { loading, items: items as BannedUser[] }
}

export function useHallRemovedSubscribersCount(hallId: string) {
  const { loading, value } = useAsyncFunction(
    () => getHallRemovedSubscribers(hallId),
    [hallId],
  )

  return { loading, count: value?.length }
}

export function useSearchedRemovedSubscribers({
  query,
  fetchedItems,
}: {
  query: string
  fetchedItems: BannedUser[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds(
    query,
    fetchedItems.map(item => item.id),
  )

  return {
    loading,
    searchedItems: searchedItems.map(id =>
      fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useHallNonAdminSubscribers(hallId: string) {
  const { items, ...other } = useHallSubscribers(hallId)
  return {
    ...other,
    items: (items as Subscriber[]).filter(({ admin }) => !admin),
  }
}

export function useSearchedHallNonAdminSubscribers(searchParams: {
  roomId: string
  query: string
  fetchedItems: Subscriber[]
  hasMoreItemToLoad: boolean
}) {
  const { searchedItems, ...other } = useSearchedSubscribers(searchParams)
  return {
    ...other,
    searchedItems: (searchedItems as Subscriber[]).filter(
      ({ admin }) => !admin,
    ),
  }
}

export function useGroupExceptionMembers(hallId: string) {
  const { items, loading } = useOpenedUserListAsyncIntegration(
    () => getHallExceptionMembers(hallId),
    [hallId],
  )

  return { items, loading }
}

export function useSearchedGroupExceptionMembers(searchParams: {
  hallId: string
  query: string
  fetchedItems: UserPermission[]
}) {
  const { loading, searchedItems } = useSearchedUserByIds(
    searchParams.query,
    searchParams.fetchedItems.map(item => item.id),
  )

  return {
    loading,
    searchedItems: searchedItems.map(id =>
      searchParams.fetchedItems.find(item => item.id === id),
    ),
  }
}

export function useClientUserPermissions(
  roomId: string,
): MemberPermissions | undefined {
  const { entity: hall } = useHall(roomId)
  const { entity: subscription } = useSubscription(roomId)

  return useMemo(
    () => decodeMemberPermissions(hall, subscription),
    [subscription?.permissions, hall?.permissions],
  )
}

export const getClientUserPermissions = async (
  roomId: string,
): Promise<MemberPermissions | undefined> => {
  const hall = await getHall(roomId)
  const subscription = await getSubscription(roomId)

  return decodeMemberPermissions(hall, subscription)
}

export const getClientUserHasPermissionToForwardMessageInHall = async (
  roomId: string,
) => {
  const hall = await getHall(roomId)
  const userPermissions = await getClientUserPermissions(roomId)
  const isAdmin = userPermissions?.isOwner || userPermissions?.isAdmin
  return !(!hall?.public && !isAdmin && shouldDisableForwardFromPrivateChannel)
}

const shouldDisableForwardFromPrivateChannel =
  hostConf.VITE_DISABLE_FORWARD_FROM_PRIVATE_CHANNEL === 'true'

export function useClientUserHasPermissionToForwardMessage(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)
  const permissions = useSubscriptionSyncPermissions(roomId)
  const isChannel = roomType === 'CHANNEL'

  return useAsyncFunction(
    isChannel
      ? () => getClientUserHasPermissionToForwardMessageInHall(roomId)
      : () => Promise.resolve(true),
    [isChannel, permissions],
  )
}

export function useSearchInSubscribers(roomId: string, query: string) {
  const [items, setItems] = useState<Subscriber[] | undefined>([])
  const [fetchedItems, setFetchedItems] = useState<Subscriber[]>([])
  const [queryFetched, setQueryFetched] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setItems(undefined)
    if (typeof queryFetched.find(q => query.startsWith(q)) === 'string') return

    setLoading(true)
    debouncedSearchInSubscribers(roomId, query, res => {
      setItems(res)
      setFetchedItems(prevState =>
        R.uniqBy(R.prop('id'), [...prevState, ...res]),
      )
      const allFetched = res.length < 40
      if (allFetched) setQueryFetched(prevState => [...prevState, query])
      setLoading(false)
    })
  }, [roomId, query])

  const fetchedIds = useMemo(
    () => fetchedItems.map(R.prop('id')),
    [fetchedItems],
  )

  const { searchedItems: localResult } = useSearchedUserByIds(query, fetchedIds)

  const result = useMemo(
    () => items || localResult.map(id => fetchedItems.find(R.propEq('id', id))),
    [items, localResult],
  )

  return { loading, items: result }
}

export function useRoomSubscriptionDate(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)
  return useAsyncFunction(
    roomType === 'CHANNEL'
      ? () => getSubscriptionDate(roomId)
      : () => Promise.resolve(undefined),
    [roomType],
  )
}
